import React, { useState, useEffect, useCallback, useRef } from "react"
import styled from "styled-components"
import { MobergFontFamily } from "../../Components/MobergFont/MobergFont"
import { MobergTheme } from "../../Components/MobergThemes/MobergColors"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../Components/MobergButton/MobergButton"
import { MobergLogoSpinner } from "../../Components/MobergLogoSpinner/MobergLogoSpinner"
import { useAuthProvider } from "../../Providers/AuthProvider"
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider"
import { MdUploadFile, MdDelete, MdRefresh, MdOpenInNew, MdWarningAmber, MdSearch, MdEventNote } from "react-icons/md"
import { ACKNOWLEDGE_OPTIONS, fmtDay, overdueCount, reviewLabel } from "./claraDocumentReview"

// The documents Clara is allowed to quote in this workspace. Uploading is an
// administrative act — an admin curates the corpus, clinicians only ask questions —
// so this lives in Backstage behind env:manage, like the Clara audit tab.

// Same convention as ClaraChat and ClaraAuditTab: the full API base including the path
// prefix, from env-config.js in deployments (path routed through the dashboard's own
// host), falling back to same-host port 5020 for local development.
const CLARA_API = window.env?.REACT_APP_CLARA_API_URL || `${window.location.protocol}//${window.location.hostname}:5020/v1/clara`

// While anything is still indexing we poll, because indexing runs in a background
// thread on the server and finishes without telling the browser.
// Mirrors clara/documents.py DOCUMENT_TYPES; the service validates, this renders.
const DOCUMENT_TYPES = ["Validated Policy", "Validated Procedure", "Guideline", "Information", "Evidence"]

/* What each type means, shown under the chips as the uploader chooses. The rule the
   descriptions teach: classify by AUTHORITY, not by format. A flowchart is a Validated
   Procedure when the site validated it; Clara detects layout on her own at ingest. */
const TYPE_DESCRIPTIONS = {
	"Validated Policy": "A hospital policy approved through your validation process.",
	"Validated Procedure": "An approved procedure or protocol staff must follow, including flowsheets, flowcharts and checklists.",
	Guideline: "Recommended practice that is not mandated, like society or unit guidelines.",
	Information: "Reference material, like manuals, education sheets or orientation documents.",
	Evidence: "Published literature, like articles, studies or textbook chapters.",
}

/* Column geometry, defined ONCE and used by both the header and every row. The header
   and rows are separate flex containers, so any column that can shrink does so by its
   own content and the two drift apart; fixed columns therefore forbid shrinking, and
   numeric columns right-align in both places. */
const COL = {
	doc: { flex: 3, minWidth: 0 },
	type: { flex: "0 0 120px" },
	status: { flex: "0 0 110px" },
	pages: { flex: "0 0 70px", textAlign: "right", alignItems: "flex-end" },
	passages: { flex: "0 0 80px", textAlign: "right", alignItems: "flex-end" },
	size: { flex: "0 0 80px", textAlign: "right", alignItems: "flex-end" },
	uploaded: { flex: "0 0 130px" },
	review: { flex: "0 0 190px" },
	actions: { flex: "0 0 88px", alignItems: "flex-end" },
}

const POLL_MS = 2000

function fmtTime(iso) {
	if (!iso) return "—"
	try {
		return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
	} catch {
		return iso
	}
}

function fmtSize(bytes) {
	if (bytes == null) return "—"
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const STATUS_LABEL = {
	pending: "Queued",
	indexing: "Indexing",
	ready: "Ready",
	failed: "Failed",
}

export function ClaraDocumentsTab() {
	const { getToken } = useAuthProvider()
	const { selectedWorkspace } = useWorkspacesProvider()

	const [documents, setDocuments] = useState([])
	const [stats, setStats] = useState({ documents: 0, chunks: 0 })
	const [indexingAvailable, setIndexingAvailable] = useState(true)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [notice, setNotice] = useState(null)
	const [uploading, setUploading] = useState(false)
	/* The upload modal's state. Flow: Upload document opens the modal; picking a file
	   reveals the type chips; choosing a chip enables Upload. The type is UTSW's own
	   nomenclature, and it is required: the site owns what counts as a policy. */
	const [uploadOpen, setUploadOpen] = useState(false)
	const [pendingFile, setPendingFile] = useState(null)
	const [docType, setDocType] = useState("")
	const [deleting, setDeleting] = useState(null)
	const fileInput = useRef(null)

	// Search here shows the PASSAGES retrieval returns, not a written answer. This tab
	// is for checking the corpus, and a fluent answer would hide a bad retrieval behind
	// good prose.
	const [question, setQuestion] = useState("")
	const [searching, setSearching] = useState(false)
	const [search, setSearch] = useState(null)

	const fetchDocuments = useCallback(async () => {
		if (!selectedWorkspace) return
		try {
			const response = await fetch(`${CLARA_API}/documents?workspace_name=${encodeURIComponent(selectedWorkspace)}`, {
				method: "GET",
				headers: { Authorization: getToken() },
			})
			if (!response.ok) throw new Error(await response.text())
			const data = await response.json()
			setDocuments(data.documents ?? [])
			setStats(data.stats ?? { documents: 0, chunks: 0 })
			setIndexingAvailable(data.indexing_available !== false)
			setError(null)
		} catch (e) {
			setError(`Could not load documents: ${e.message}`)
		} finally {
			setLoading(false)
		}
	}, [getToken, selectedWorkspace])

	useEffect(() => {
		setLoading(true)
		fetchDocuments()
	}, [fetchDocuments])

	// Poll only while something is actually indexing, then stop. A permanent timer here
	// would keep a Backstage tab hitting the server all day for no reason.
	const busy = documents.some(d => d.status === "pending" || d.status === "indexing")

	// Escape closes the upload modal, but never mid-upload: abandoning a request in
	// flight leaves a pending document row with nobody watching it finish.
	useEffect(() => {
		if (!uploadOpen) return undefined
		const onKeyDown = event => {
			if (event.key === "Escape" && !uploading) closeUploadModal()
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	})
	useEffect(() => {
		if (!busy) return undefined
		const timer = setInterval(fetchDocuments, POLL_MS)
		return () => clearInterval(timer)
	}, [busy, fetchDocuments])

	function pickFile(event) {
		const file = event.target.files?.[0]
		event.target.value = "" // let the same file be re-picked after a failure
		if (file) setPendingFile(file)
	}

	function closeUploadModal() {
		setUploadOpen(false)
		setPendingFile(null)
		setDocType("")
	}

	async function handleUpload() {
		if (!pendingFile || !docType) return

		setUploading(true)
		setError(null)
		setNotice(null)
		try {
			const body = new FormData()
			body.append("file", pendingFile)
			body.append("document_type", docType)
			const response = await fetch(`${CLARA_API}/documents/upload?workspace_name=${encodeURIComponent(selectedWorkspace)}`, {
				method: "POST",
				headers: { Authorization: getToken() },
				body,
			})
			const data = await response.json().catch(() => ({}))
			if (!response.ok) throw new Error(data.error || `Upload failed (${response.status})`)
			if (data.duplicate) setNotice("That document is already uploaded.")
			closeUploadModal()
			await fetchDocuments()
		} catch (e) {
			setError(e.message)
		} finally {
			setUploading(false)
		}
	}

	const [reviewing, setReviewing] = useState(null)
	const [reviewDraft, setReviewDraft] = useState({ effective: "", due: "", remindDays: 90 })
	const [savingReview, setSavingReview] = useState(false)
	const [overdueOnly, setOverdueOnly] = useState(false)
	const [rescanning, setRescanning] = useState(false)

	function openReview(document) {
		setReviewing(document)
		setReviewDraft({ effective: document.effective_date || "", due: document.next_review_date || "", remindDays: 90 })
	}

	async function saveReview(body) {
		setSavingReview(true)
		setError(null)
		try {
			const response = await fetch(`${CLARA_API}/documents/${reviewing.id}/review`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ workspace_name: selectedWorkspace, ...body }),
			})
			if (!response.ok) throw new Error(await response.text())
			setReviewing(null)
			await fetchDocuments()
		} catch (e) {
			setError(`Could not update review dates: ${e.message}`)
		} finally {
			setSavingReview(false)
		}
	}

	async function handleRescanDates() {
		setRescanning(true)
		setError(null)
		try {
			const response = await fetch(`${CLARA_API}/documents/rescan-dates`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ workspace_name: selectedWorkspace }),
			})
			if (!response.ok) throw new Error(await response.text())
			const result = await response.json()
			setNotice(`Read dates for ${result.updated} of ${result.scanned} document${result.scanned === 1 ? "" : "s"} missing them.`)
			await fetchDocuments()
		} catch (e) {
			setError(`Could not read dates: ${e.message}`)
		} finally {
			setRescanning(false)
		}
	}

	async function handleDelete(document) {
		// Deleting removes every passage indexed from it, so Clara stops being able to
		// quote the document at all. Worth one confirmation.
		if (!window.confirm(`Delete "${document.title}"? Clara will no longer be able to answer from it.`)) return
		setDeleting(document.id)
		setError(null)
		try {
			const response = await fetch(`${CLARA_API}/documents/${document.id}/delete`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ workspace_name: selectedWorkspace }),
			})
			if (!response.ok) throw new Error(await response.text())
			await fetchDocuments()
		} catch (e) {
			setError(`Could not delete: ${e.message}`)
		} finally {
			setDeleting(null)
		}
	}

	async function handleSearch(event) {
		event?.preventDefault()
		if (!question.trim()) return
		setSearching(true)
		setError(null)
		try {
			const response = await fetch(`${CLARA_API}/documents/search`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: getToken() },
				body: JSON.stringify({ workspace_name: selectedWorkspace, question }),
			})
			const data = await response.json().catch(() => ({}))
			if (!response.ok) throw new Error(data.error || `Search failed (${response.status})`)
			setSearch(data)
		} catch (e) {
			setError(e.message)
			setSearch(null)
		} finally {
			setSearching(false)
		}
	}

	if (loading) {
		return (
			<Center>
				<MobergLogoSpinner />
			</Center>
		)
	}

	return (
		<Wrap>
			<Toolbar>
				<MobergColumnish>
					<ToolbarCount>
						{stats.documents} document{stats.documents === 1 ? "" : "s"} &middot; {stats.chunks} indexed passage{stats.chunks === 1 ? "" : "s"}
					</ToolbarCount>
					<ToolbarHint>Clara can quote these when answering questions in this workspace.</ToolbarHint>
				</MobergColumnish>

				<Buttons>
					<MobergButton onClick={fetchDocuments} variant={MobergButtonVariant.OUTLINE} theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE}>
						<MdRefresh size={16} /> Refresh
					</MobergButton>
					<MobergButton
						onClick={handleRescanDates}
						variant={MobergButtonVariant.OUTLINE}
						theme={MobergTheme.BLUE}
						shape={MobergButtonShape.RECTANGLE}
						disabled={rescanning || stats.documents === 0}
					>
						<MdEventNote size={16} /> {rescanning ? "Reading dates…" : "Read review dates"}
					</MobergButton>
					<MobergButton
						onClick={() => setUploadOpen(true)}
						variant={MobergButtonVariant.FILLED}
						theme={MobergTheme.BLUE}
						shape={MobergButtonShape.RECTANGLE}
						disabled={uploading || !indexingAvailable}
					>
						<MdUploadFile size={16} /> Upload document
					</MobergButton>
				</Buttons>
			</Toolbar>

			{!indexingAvailable && (
				<Banner $tone="warn">
					<MdWarningAmber size={18} />
					Document indexing is unavailable: this deployment has no OpenAI API key configured. Uploading is disabled
					rather than producing documents that look indexed but cannot be searched.
				</Banner>
			)}
			{error && <Banner $tone="error">{error}</Banner>}
			{notice && <Banner $tone="info">{notice}</Banner>}
			{overdueCount(documents) > 0 && (
				<Banner $tone="error">
					<MdWarningAmber size={18} />
					{overdueCount(documents)} document{overdueCount(documents) === 1 ? " is" : "s are"} past the review date printed on
					{overdueCount(documents) === 1 ? " it" : " them"}. Clara warns when she answers from {overdueCount(documents) === 1 ? "it" : "them"}.
					<FilterToggle type="button" onClick={() => setOverdueOnly(value => !value)}>
						{overdueOnly ? "Show all" : "Show only these"}
					</FilterToggle>
				</Banner>
			)}

			{stats.documents > 0 && (
				<SearchPanel onSubmit={handleSearch}>
					<SearchRow>
						<SearchInput
							value={question}
							onChange={e => setQuestion(e.target.value)}
							placeholder="Ask something a nurse would ask, e.g. “Are nurses allowed to access Ommaya reservoirs?”"
							aria-label="Test a question against these documents"
						/>
						<MobergButton type="submit" theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE} disabled={searching || !question.trim()}>
							<MdSearch size={16} /> {searching ? "Searching…" : "Test search"}
						</MobergButton>
					</SearchRow>
					<SearchHint>
						Shows the passages Clara would read, best first, so you can check the documents cover what people
						will ask. It does not write an answer.
					</SearchHint>

					{search && (
						<Results>
							<ResultsMeta>
								{search.results.length === 0
									? "Nothing in these documents matched."
									: `${search.results.length} passage${search.results.length === 1 ? "" : "s"} · ${search.latency_ms} ms`}
							</ResultsMeta>
							{search.results.map((hit, index) => (
								<Result key={hit.chunk_id}>
									<ResultHead>
										<Rank>{index + 1}</Rank>
										<ResultCitation>{hit.citation}</ResultCitation>
										<Scores>
											{hit.rerank_score != null && <Score $strong>relevance {hit.rerank_score}/10</Score>}
											<Score>similarity {hit.vector_score}</Score>
											<Score>keyword {hit.keyword_score}</Score>
										</Scores>
									</ResultHead>
									<ResultText>{hit.text}</ResultText>
								</Result>
							))}
						</Results>
					)}
				</SearchPanel>
			)}

			<ListHeader>
				<HCell style={COL.doc}>Document</HCell>
				<HCell style={COL.type}>Type</HCell>
				<HCell style={COL.status}>Status</HCell>
				<HCell style={COL.pages}>Pages</HCell>
				<HCell style={COL.passages}>Passages</HCell>
				<HCell style={COL.size}>Size</HCell>
				<HCell style={COL.uploaded}>Uploaded</HCell>
				<HCell style={COL.review}>Review</HCell>
				<HCell style={COL.actions} />
			</ListHeader>

			<ListBody>
				{documents.length === 0 && (
					<Empty>
						No documents yet. Upload a policy PDF and Clara will be able to answer from it, quoting the page it
						came from.
					</Empty>
				)}

				{documents.filter(document => !overdueOnly || document.review_status === "overdue").map(document => (
					<Row key={document.id}>
						<Cell style={COL.doc}>
							<Title>{document.title}</Title>
							<Sub>
								{document.doc_number && <Mono>{document.doc_number}</Mono>}
								{document.doc_number && " · "}
								{document.filename}
								{document.source_url && (
									<>
										{" · "}
										<SourceLink href={document.source_url} target="_blank" rel="noreferrer">
											source <MdOpenInNew size={12} />
										</SourceLink>
									</>
								)}
							</Sub>
							{document.status === "failed" && document.error && <Failure>{document.error}</Failure>}
						</Cell>

						<Cell style={COL.type}>
							<TypeLabel $unclassified={!DOCUMENT_TYPES.includes(document.doc_type)}>
								{DOCUMENT_TYPES.includes(document.doc_type) ? document.doc_type : "Unclassified"}
							</TypeLabel>
						</Cell>

						<Cell style={COL.status}>
							<StatusPill $status={document.status}>{STATUS_LABEL[document.status] ?? document.status}</StatusPill>
						</Cell>

						<Cell style={COL.pages}>
							<Num>{document.page_count ?? "—"}</Num>
						</Cell>
						<Cell style={COL.passages}>
							<Num>{document.chunk_count ?? "—"}</Num>
						</Cell>
						<Cell style={COL.size}>
							<Num>{fmtSize(document.byte_size)}</Num>
						</Cell>
						<Cell style={COL.uploaded}>
							<Muted>{fmtTime(document.created_at)}</Muted>
						</Cell>

						<Cell style={COL.review}>
							<ReviewPill $tone={reviewLabel(document).tone}>{reviewLabel(document).text}</ReviewPill>
							{document.effective_date && <Muted>Effective {fmtDay(document.effective_date)}</Muted>}
						</Cell>

						<Cell style={{ ...COL.actions, flexDirection: "row", gap: 6, justifyContent: "flex-end" }}>
							<MobergButton
								onClick={() => openReview(document)}
								variant={MobergButtonVariant.OUTLINE}
								theme={MobergTheme.BLUE}
								shape={MobergButtonShape.SQUARE}
								ariaLabel={`Review dates for ${document.title}`}
								tooltip="Review dates"
							>
								<MdEventNote size={16} />
							</MobergButton>
							<MobergButton
								onClick={() => handleDelete(document)}
								variant={MobergButtonVariant.OUTLINE}
								theme={MobergTheme.RED}
								shape={MobergButtonShape.SQUARE}
								disabled={deleting === document.id}
								ariaLabel={`Delete ${document.title}`}
								tooltip="Delete"
							>
								<MdDelete size={16} />
							</MobergButton>
						</Cell>
					</Row>
				))}
			</ListBody>

			{reviewing && (
				<ModalOverlay onClick={() => !savingReview && setReviewing(null)}>
					<Modal onClick={e => e.stopPropagation()} role="dialog" aria-label="Review dates">
						<ModalTitle>Review dates</ModalTitle>
						<ModalHint>
							{reviewing.title}. Clara reads these from the document when it is uploaded; correct them here if
							they are wrong or missing.
						</ModalHint>
						<DateRow>
							<DateField>
								<span>Effective date</span>
								<input type="date" value={reviewDraft.effective} onChange={e => setReviewDraft(d => ({ ...d, effective: e.target.value }))} />
							</DateField>
							<DateField>
								<span>Next review due</span>
								<input type="date" value={reviewDraft.due} onChange={e => setReviewDraft(d => ({ ...d, due: e.target.value }))} />
							</DateField>
						</DateRow>
						{reviewing.review_status === "overdue" && (
							<AckBox>
								<strong>No newer version?</strong> Confirm this is the latest version you have. Clara will stop
								warning about it, and the warning comes back after
								<select
									value={reviewDraft.remindDays}
									onChange={e => setReviewDraft(d => ({ ...d, remindDays: Number(e.target.value) }))}
									aria-label="Remind me after"
								>
									{ACKNOWLEDGE_OPTIONS.map(days => <option key={days} value={days}>{days} days</option>)}
								</select>
								<MobergButton
									onClick={() => saveReview({ acknowledge_days: reviewDraft.remindDays })}
									variant={MobergButtonVariant.OUTLINE}
									theme={MobergTheme.BLUE}
									shape={MobergButtonShape.RECTANGLE}
									disabled={savingReview}
								>
									Mark as latest version
								</MobergButton>
							</AckBox>
						)}
						<ModalFooter>
							<MobergButton onClick={() => setReviewing(null)} variant={MobergButtonVariant.OUTLINE} theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE} disabled={savingReview}>
								Cancel
							</MobergButton>
							<MobergButton
								onClick={() => saveReview({ effective_date: reviewDraft.effective || null, next_review_date: reviewDraft.due || null })}
								theme={MobergTheme.BLUE}
								shape={MobergButtonShape.RECTANGLE}
								disabled={savingReview}
							>
								{savingReview ? "Saving…" : "Save dates"}
							</MobergButton>
						</ModalFooter>
					</Modal>
				</ModalOverlay>
			)}

			{uploadOpen && (
				<ModalOverlay onClick={() => !uploading && closeUploadModal()}>
					<Modal onClick={e => e.stopPropagation()} role="dialog" aria-label="Upload a document">
						<ModalTitle>Upload a document</ModalTitle>
						<ModalHint>Clara answers from these, quoting the page. PDF only.</ModalHint>

						{/* Step 1: the file. */}
						{pendingFile ? (
							<PickedFile>
								<MdUploadFile size={17} />
								<PickedName title={pendingFile.name}>{pendingFile.name}</PickedName>
								<PickedMeta>{fmtSize(pendingFile.size)}</PickedMeta>
								<ChangeFile type="button" onClick={() => fileInput.current?.click()} disabled={uploading}>
									Change
								</ChangeFile>
							</PickedFile>
						) : (
							<DropZone type="button" onClick={() => fileInput.current?.click()}>
								<MdUploadFile size={22} />
								Choose a PDF
							</DropZone>
						)}
						<HiddenInput ref={fileInput} type="file" accept="application/pdf,.pdf" onChange={pickFile} />

						{/* Step 2: the classification, only once there is a file. The site owns
						    what counts as a policy, so there is no default chip. */}
						{pendingFile && (
							<>
								<ChipLabel>What kind of document is this?</ChipLabel>
								<ChipRow>
									{DOCUMENT_TYPES.map(t => (
										<TypeChip
											key={t}
											type="button"
											$selected={docType === t}
											onClick={() => setDocType(t)}
											disabled={uploading}
											title={TYPE_DESCRIPTIONS[t]}
										>
											{t}
										</TypeChip>
									))}
								</ChipRow>
								<ChipHint>
									{docType
										? TYPE_DESCRIPTIONS[docType]
										: "Choose by the document's authority, not its format. A flowchart or flowsheet your site has approved is a Validated Procedure; Clara reads the layout on her own."}
								</ChipHint>
							</>
						)}

						<ModalFooter>
							<MobergButton
								onClick={closeUploadModal}
								variant={MobergButtonVariant.OUTLINE}
								theme={MobergTheme.BLUE}
								shape={MobergButtonShape.RECTANGLE}
								disabled={uploading}
							>
								Cancel
							</MobergButton>
							<MobergButton
								onClick={handleUpload}
								variant={MobergButtonVariant.FILLED}
								theme={MobergTheme.BLUE}
								shape={MobergButtonShape.RECTANGLE}
								disabled={!pendingFile || !docType || uploading}
								title={!pendingFile ? "Choose a PDF first" : !docType ? "Choose a document type" : undefined}
							>
								{uploading ? "Uploading…" : "Upload"}
							</MobergButton>
						</ModalFooter>
					</Modal>
				</ModalOverlay>
			)}
		</Wrap>
	)
}

const Wrap = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
`

const Center = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 200px;
`

const Toolbar = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	padding: 4px 8px 12px;
`

const MobergColumnish = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`

const Buttons = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
`

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(16, 24, 40, 0.45);
`

const Modal = styled.div`
	width: min(480px, calc(100vw - 32px));
	padding: 22px;
	border-radius: 12px;
	background: var(--color-surface-panel, #fff);
	box-shadow: 0 18px 50px rgba(16, 24, 40, 0.28);
	display: flex;
	flex-direction: column;
	gap: 12px;
`

const ModalTitle = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 16px;
	font-weight: 650;
	color: var(--text-color);
`

const ModalHint = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: var(--text-secondary-color);
	margin-top: -6px;
`

const DropZone = styled.button`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	padding: 26px 12px;
	border: 1.5px dashed var(--color-border-default);
	border-radius: 10px;
	background: transparent;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13.5px;
	color: var(--text-secondary-color);
	cursor: pointer;

	&:hover {
		border-color: var(--color-accent-interactive);
		color: var(--text-color);
	}
`

const PickedFile = styled.div`
	display: flex;
	align-items: center;
	gap: 9px;
	padding: 12px 14px;
	border: 1px solid var(--color-border-light);
	border-radius: 10px;
	font-family: ${MobergFontFamily.REGULAR};
	color: var(--text-color);
`

const PickedName = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 13.5px;
`

const PickedMeta = styled.span`
	flex-shrink: 0;
	font-size: 12px;
	color: var(--text-secondary-color);
`

const ChangeFile = styled.button`
	flex-shrink: 0;
	margin-left: auto;
	border: none;
	background: transparent;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: var(--color-accent-interactive);
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`

const ChipLabel = styled.div`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	font-weight: 600;
	color: var(--text-color);
`

const ChipRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`

const TypeChip = styled.button`
	padding: 7px 13px;
	border-radius: 999px;
	border: 1.5px solid ${p => (p.$selected ? "var(--color-accent-interactive)" : "var(--color-border-default)")};
	background: ${p => (p.$selected ? "var(--color-accent-interactive)" : "transparent")};
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: ${p => (p.$selected ? "#fff" : "var(--text-color)")};
	cursor: pointer;
	transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;

	&:hover {
		border-color: var(--color-accent-interactive);
	}
`

const ChipHint = styled.div`
	min-height: 32px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	line-height: 1.45;
	color: var(--text-secondary-color);
`

const ModalFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	margin-top: 6px;
`

const TypeLabel = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: ${p => (p.$unclassified ? "var(--text-secondary-color)" : "var(--text-color)")};
	font-style: ${p => (p.$unclassified ? "italic" : "normal")};
`

const HiddenInput = styled.input`
	display: none;
`

const ToolbarCount = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	color: var(--text-secondary-color);
`

const ToolbarHint = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
	opacity: 0.8;
`

const Banner = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0 8px 12px;
	padding: 10px 16px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	border-radius: 6px;
	/* --color-danger-text-hover, not --color-danger-text: the lighter red measured about
	   2.9:1 on its tinted background, too faint for the line that says policies are out
	   of date (UI review, 2 Oct). */
	color: ${({ $tone }) => ($tone === "error" ? "var(--color-danger-text-hover)" : $tone === "warn" ? "var(--text-color)" : "var(--text-secondary-color)")};
	background: ${({ $tone }) => ($tone === "error" ? "var(--color-danger-hover-bg)" : "var(--color-hover-neutral)")};
	border: 1px solid ${({ $tone }) => ($tone === "error" ? "var(--color-danger-text)" : "var(--color-border-default)")};
`

const ListHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 8px;
	border-bottom: 1px solid var(--color-border-default);
`

const HCell = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: var(--text-secondary-color);
`

const ListBody = styled.div`
	flex: 1;
	overflow-y: auto;
`

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 8px;
	border-bottom: 1px solid var(--color-border-default);
`

const Cell = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 3px;
	min-width: 0;
`

const Title = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: var(--text-color);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const Sub = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const Mono = styled.span`
	font-family: monospace;
	font-size: 11px;
`

const SourceLink = styled.a`
	color: var(--text-secondary-color);
	text-decoration: underline;
	display: inline-flex;
	align-items: center;
	gap: 2px;
`

const Failure = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--color-danger-text);
`

const Num = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	color: var(--text-color);
	font-variant-numeric: tabular-nums;
`

const Muted = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
`

const Empty = styled.div`
	padding: 40px 16px;
	text-align: center;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: var(--text-secondary-color);
`

const StatusPill = styled.span`
	align-self: flex-start;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 10px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	padding: 2px 8px;
	border-radius: 10px;
	color: ${({ $status }) => ($status === "ready" ? "var(--color-success-text)" : $status === "failed" ? "var(--color-danger-text)" : "var(--text-secondary-color)")};
	background: ${({ $status }) => ($status === "ready" ? "var(--color-success-hover-bg)" : $status === "failed" ? "var(--color-danger-hover-bg)" : "var(--color-hover-neutral)")};
`

const ReviewPill = styled.span`
	align-self: flex-start;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 11px;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 10px;
	color: ${({ $tone }) => ($tone === "overdue" ? "var(--color-danger-text-hover)" : $tone === "current" ? "var(--color-success-text)" : "var(--text-secondary-color)")};
	background: ${({ $tone }) => ($tone === "overdue" ? "var(--color-danger-hover-bg)" : $tone === "current" ? "var(--color-success-hover-bg)" : "var(--color-hover-neutral)")};
`

const FilterToggle = styled.button`
	margin-left: auto;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	padding: 4px 10px;
	border-radius: 6px;
	border: 1px solid currentColor;
	background: transparent;
	color: inherit;
	cursor: pointer;
`

const DateRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	margin: 12px 0;
`

const DateField = styled.label`
	display: flex;
	flex-direction: column;
	gap: 4px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	color: var(--text-secondary-color);

	input {
		font-size: 14px;
		padding: 6px 8px;
		border-radius: 6px;
		border: 1px solid var(--color-border-default);
		background: var(--color-input-bg, #fff);
		color: var(--text-color);
	}
`

const AckBox = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin: 4px 0 12px;
	padding: 12px 14px;
	border-radius: 8px;
	background: var(--color-hover-neutral);
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	color: var(--text-color);

	select {
		font-size: 13px;
		padding: 3px 6px;
		border-radius: 6px;
		border: 1px solid var(--color-border-default);
		background: var(--color-input-bg, #fff);
		color: var(--text-color);
	}
`

const SearchPanel = styled.form`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin: 0 8px 16px;
	padding: 14px 16px;
	border: 1px solid var(--color-border-default);
	border-radius: 8px;
	background: var(--color-hover-neutral);
`

const SearchRow = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
`

const SearchInput = styled.input`
	flex: 1;
	min-width: 0;
	padding: 9px 12px;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: var(--text-color);
	background: var(--color-input-bg);
	border: 1px solid var(--color-border-default);
	border-radius: 6px;

	&:focus {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: 1px;
	}
`

const SearchHint = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
`

const Results = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 10px;
	max-height: 420px;
	overflow-y: auto;
`

const ResultsMeta = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	color: var(--text-secondary-color);
`

const Result = styled.div`
	padding: 10px 12px;
	border: 1px solid var(--color-border-default);
	border-radius: 6px;
	background: var(--color-surface-panel);
`

const ResultHead = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	margin-bottom: 6px;
`

const Rank = styled.span`
	font-family: monospace;
	font-size: 11px;
	font-weight: 700;
	color: var(--text-secondary-color);
	min-width: 16px;
`

const ResultCitation = styled.span`
	flex: 1;
	min-width: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12px;
	font-weight: 600;
	color: var(--text-color);
`

const Scores = styled.div`
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
`

const Score = styled.span`
	font-family: monospace;
	font-size: 10px;
	padding: 2px 6px;
	border-radius: 8px;
	color: ${({ $strong }) => ($strong ? "var(--color-success-text)" : "var(--text-secondary-color)")};
	background: ${({ $strong }) => ($strong ? "var(--color-success-hover-bg)" : "var(--color-hover-neutral)")};
`

const ResultText = styled.p`
	margin: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	line-height: 1.5;
	color: var(--text-secondary-color);
`
